import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Table,
  Row,
  Col,
} from "reactstrap";
import AddItemModal from "../modals/addItemsModal.js";
import DeleteItemModal from "../modals/deleteModal.js";
import ButtonGroupComponent from "components/Buttons/ButtonGroups.js";
import NotificationComponent from "../components/Alert/alert.js";
import { DataTableShell } from "components/ui/TableDataShell.js";
import { useTranslation } from "../hooks/useTranslation";

// RTK Query: ensure your barrel re-exports useFetchPanelTableQuery
// from cmsPanelsApi (or import directly from "../api/cmsPanelsApi")
import { useFetchPanelTableQuery } from "../api/api";

function CMSPanel() {
  const t = useTranslation();
  const notificationComponentRef = useRef(new NotificationComponent());

  // one active tab → one active query
  const [activeButton, setActiveButton] = useState("brands");
  const [tableData, setTableData] = useState([]);

  const {
    data = [],
    isFetching,
    isError,
  } = useFetchPanelTableQuery(activeButton, {
    // Turn off auto refetch while you stabilize UI behavior.
    refetchOnFocus: false,
    refetchOnReconnect: false,
  });

  useEffect(() => {
    setTableData(Array.isArray(data) ? data : []);
  }, [data]);

  const [modalOpen, setModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [modalItemType, setModalItemType] = useState("");
  const [editItemData, setEditItemData] = useState({});

  const handleButtonClick = (button) => {
    if (button !== activeButton) setActiveButton(button);
  };

  const toggleModal = (itemType) => {
    setEditItemData({});
    setModalItemType(itemType);
    setModalOpen(!modalOpen);
  };

  // keeps your optimistic add behavior; RTK mutations can replace this later
  const addNewItem = (formData) => {
    const updatedTableData = [...tableData, formData];
    setTableData(updatedTableData);
  };

  const renderTableHeaders = () => {
    switch (activeButton) {
      case "colors":
        return [
          t("table.id"),
          t("table.colorName"),
          t("table.hexCode"),
          t("table.albanian"),
          t("table.english"),
          t("table.turkish"),
          t("table.actions"),
        ];
      case "brands":
        return [
          t("table.id"),
          t("table.brand"),
          t("table.produced"),
          t("table.type"),
          t("table.actions"),
        ];
      case "numbers":
        return [
          t("table.id"),
          t("table.number"),
          t("table.type"),
          t("table.actions"),
        ];
      case "types":
        return [
          t("table.id"),
          "Cover",
          t("table.specificType"),
          "Slug",
          t("table.type"),
          "Storefront",
          "Order",
          t("table.actions"),
        ];
      case "locations":
        return [t("table.id"), t("table.locations"), t("table.actions")];
      default:
        return [];
    }
  };

  const openEditModal = (item) => {
    setModalItemType(activeButton);
    setEditItemData(item);
    setModalOpen(true);
  };

  const openDeleteModal = (item) => {
    setModalItemType(activeButton);
    setEditItemData(item);
    setDeleteModalOpen(true);
  };
  const headersByTab = {
    colors: [
      t("table.id"),
      t("table.colorName"),
      t("table.hexCode"),
      t("table.albanian"),
      t("table.english"),
      t("table.turkish"),
      t("table.actions"),
    ],
    brands: [
      t("table.id"),
      t("table.brand"),
      t("table.produced"),
      t("table.type"),
      t("table.actions"),
    ],
    numbers: [
      t("table.id"),
      t("table.number"),
      t("table.type"),
      t("table.actions"),
    ],
    types: [
      t("table.id"),
      "Cover",
      t("table.specificType"),
      "Slug",
      t("table.type"),
      "Storefront",
      "Order",
      t("table.actions"),
    ],
    locations: [t("table.id"), t("table.locations"), t("table.actions")],
  };

  const headersUI = headersByTab[activeButton] || [];
  const rowRenderer = (item, rowIdx) => {
    // Per-tab safe key access; fallback “—” if missing
    const cell = (k) => String(item?.[k] ?? "—");

    switch (activeButton) {
      case "colors":
        return (
          <tr key={rowIdx}>
            <td>{cell("id")}</td>
            <td>{cell("colorName")}</td>
            <td>
              {item?.hexCode ? (
                <span className="d-inline-flex align-items-center gap-2">
                  <span
                    aria-hidden="true"
                    style={{
                      width: "18px",
                      height: "18px",
                      borderRadius: "999px",
                      border: "1px solid rgba(17, 24, 39, 0.18)",
                      background: item.hexCode,
                      display: "inline-block",
                    }}
                  />
                  <span>{item.hexCode}</span>
                </span>
              ) : (
                "—"
              )}
            </td>
            <td>{cell("albanianName")}</td>
            <td>{cell("englishName")}</td>
            <td>{cell("turkishName")}</td>
            <td align="right">
              <Button color="link" onClick={() => openEditModal(item)}>
                <i className="tim-icons icon-pencil" />
              </Button>
              <Button color="link" onClick={() => openDeleteModal(item)}>
                <i className="tim-icons icon-trash-simple" />
              </Button>
            </td>
          </tr>
        );
      case "brands":
        return (
          <tr key={rowIdx}>
            <td>{cell("id")}</td>
            <td>{cell("brandName")}</td>
            <td>{cell("produced")}</td>
            <td>{cell("type")}</td>
            <td align="right">
              <Button color="link" onClick={() => openEditModal(item)}>
                <i className="tim-icons icon-pencil" />
              </Button>
              <Button color="link" onClick={() => openDeleteModal(item)}>
                <i className="tim-icons icon-trash-simple" />
              </Button>
            </td>
          </tr>
        );
      case "numbers":
        return (
          <tr key={rowIdx}>
            <td>{cell("id")}</td>
            <td>{cell("number")}</td>
            <td>{cell("type")}</td>
            <td align="right">
              <Button color="link" onClick={() => openEditModal(item)}>
                <i className="tim-icons icon-pencil" />
              </Button>
              <Button color="link" onClick={() => openDeleteModal(item)}>
                <i className="tim-icons icon-trash-simple" />
              </Button>
            </td>
          </tr>
        );
      case "types":
        return (
          <tr key={rowIdx}>
            <td>{cell("id")}</td>
            <td>
              {item?.coverImageUrl ? (
                <img
                  src={item.coverImageUrl}
                  alt={item.specificType || "Cover"}
                  style={{
                    width: "38px",
                    height: "50px",
                    objectFit: "cover",
                    borderRadius: "6px",
                  }}
                />
              ) : (
                "—"
              )}
            </td>
            <td>{cell("specificType")}</td>
            <td>{cell("slug")}</td>
            <td>{cell("type")}</td>
            <td>
              {item?.isPublished || item?.publishedInStorefront ? (
                <span className="badge badge-success">Published</span>
              ) : (
                <span className="badge badge-secondary">Hidden</span>
              )}
            </td>
            <td>{cell("sortOrder")}</td>
            <td align="right">
              <Button color="link" onClick={() => openEditModal(item)}>
                <i className="tim-icons icon-pencil" />
              </Button>
              <Button color="link" onClick={() => openDeleteModal(item)}>
                <i className="tim-icons icon-trash-simple" />
              </Button>
            </td>
          </tr>
        );
      case "locations":
        return (
          <tr key={rowIdx}>
            <td>{cell("id")}</td>
            <td>{cell("name")}</td>
            <td align="right">
              <Button color="link" onClick={() => openEditModal(item)}>
                <i className="tim-icons icon-pencil" />
              </Button>
              <Button color="link" onClick={() => openDeleteModal(item)}>
                <i className="tim-icons icon-trash-simple" />
              </Button>
            </td>
          </tr>
        );
      default:
        // Generic fallback for unexpected tabs/shape
        return (
          <tr key={rowIdx}>
            {Object.keys(item).map((k, i) => (
              <td key={i}>{String(item[k])}</td>
            ))}
            <td align="right">
              <Button color="link" onClick={() => openEditModal(item)}>
                <i className="tim-icons icon-pencil" />
              </Button>
              <Button color="link" onClick={() => openDeleteModal(item)}>
                <i className="tim-icons icon-trash-simple" />
              </Button>
            </td>
          </tr>
        );
    }
  };

  return (
    <div className="content">
      <NotificationComponent ref={notificationComponentRef} />

      <Card className="card-chart cms-panels-card">
        <CardHeader>
          <Row>
            <Col className="text-left" sm="6">
              <h5 className="card-category">CMS</h5>
              <CardTitle tag="h2">{t("cms.title")}</CardTitle>
            </Col>

            <Col sm="6">
              <ButtonGroupComponent
                activeButton={activeButton}
                onButtonClick={handleButtonClick}
                buttons={["brands", "colors", "numbers", "types", "locations"]}
                size="md"
              />
            </Col>

            <Col sm="12">
              <Button
                color="primary"
                size="md"
                onClick={() => toggleModal(activeButton)}
                className="float-right cms-add-primary"
                disabled={!activeButton}
              >
                {t("cms.addNew")}
              </Button>
            </Col>
          </Row>
        </CardHeader>

        <CardBody>
          <DataTableShell
            headers={headersUI}
            rows={isError ? [] : tableData}
            isLoading={isFetching && tableData.length === 0}
            emptyProps={
              isError
                ? {
                    title: t("messages.failedToLoad"),
                    hint: t("messages.pleaseTryAgain"),
                    icon: "tim-icons icon-alert-circle-exc",
                    ctaLabel: t("messages.retry"),
                    onCta: () => window.location.reload(), // or expose refetch if you have it
                  }
                : {
                    title: t("messages.noData"),
                    hint:
                      t("cms.addFirstItem") ||
                      "Add your first item to populate this table.",
                    icon: "tim-icons icon-simple-add",
                    ctaLabel: t("cms.addNew"),
                    onCta: () => toggleModal(activeButton),
                  }
            }
            rowRenderer={rowRenderer}
          />
        </CardBody>
      </Card>

      <AddItemModal
        modalOpen={modalOpen}
        toggleModal={() => setModalOpen(!modalOpen)}
        itemType={modalItemType}
        notificationComponentRef={notificationComponentRef}
        addNewItem={addNewItem}
        setTableData={setTableData}
        tableData={tableData}
        editItemData={editItemData}
        setEditItemData={setEditItemData}
      />

      <DeleteItemModal
        modalOpen={deleteModalOpen}
        notificationComponentRef={notificationComponentRef}
        toggleModal={() => setDeleteModalOpen(!deleteModalOpen)}
        itemType={modalItemType}
        setTableData={setTableData}
        tableData={tableData}
        editItemData={editItemData}
        setEditItemData={setEditItemData}
      />
    </div>
  );
}

export default CMSPanel;
